import { inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ConfirmService, WorkflowService, DATA_PROVIDER, AUTH_CONTEXT, takeoverPreviousProject, takeoverHadInterimWork } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from '../../data-model';
import { ProjectService } from '../../project.service';
import { applyTakeoverMove, applyTakeoverRevert } from './point-takeover';

/** Ergebnis einer Übernahme — `doc` enthält das aktualisierte Punkt-Dokument. */
export interface FreigabeResult {
	ok: boolean;
	doc?: AufnahmePunktDocument;
}

/**
 * Orchestriert die Übernahme eines Altpunkts (Bestand `null` oder `abgeschlossen`)
 * in das aktive Projekt — der gemeinsame Code-Pfad für den Toolbar-Button
 * (Entwickler, editierbar) und den Banner-Button (Manager + Entwickler-Bypass).
 * Owner ohne Manager-Rolle hat seit 2026-05-26 keine Freigabe-Capability
 * (siehe `canIfreigeben`).
 *
 * **Einfachmodell (PLAN-single-membership):** Die Übernahme **hängt um** statt
 * hinzuzufügen — der Punkt gehört danach zu **genau einem** Projekt
 * (`header.projects = [aktiv]`). Der Workflow wird ins aktive Projekt
 * zurückgesetzt (`restartWorkflow`), der Entzug aus dem alten Projekt wird
 * namentlich im Reset-Marker (`fromProjectName`) festgehalten, und die
 * abgeleiteten Status der betroffenen Projekte (alt + neu) werden nachgezogen.
 *
 * I/O läuft bewusst über die Lib-Tokens `DATA_PROVIDER`/`AUTH_CONTEXT` (nicht
 * über DataService/AuthService direkt) — analog WorkflowService, damit der
 * Service ohne den schweren bpweb-Import-Graph testbar bleibt.
 *
 * Voraussetzungen (Sichtbarkeit des Buttons) prüfen die Aufrufer über
 * `ProjectService.canIfreigeben()` + Altpunkt-Status — hier wird nur ausgeführt.
 */
@Injectable({ providedIn: 'root' })
export class PointFreigabeService {
	private confirmService = inject(ConfirmService);
	private workflow = inject(WorkflowService);
	private ps = inject(ProjectService);
	private toastr = inject(ToastrService);
	private data = inject(DATA_PROVIDER) as { get(id: string): Promise<any>; put(doc: any): Promise<any> };
	private auth = inject(AUTH_CONTEXT) as { currentUser: string; currentUserId?: string };

	async freigeben(bp: AufnahmePunktDocument): Promise<FreigabeResult> {
		const projectId = this.ps.project();
		if (!projectId) {
			this.toastr.error('Kein aktives Projekt.');
			return { ok: false };
		}
		const projectName = this.ps.currentProjectDoc()?.name ?? '';

		// Frischen Stand laden — der lokale `bp` kann veraltet sein, und wir
		// schreiben Workflow + header.projects in einem Rutsch.
		let fresh: AufnahmePunktDocument;
		try {
			fresh = await this.data.get(bp._id) as AufnahmePunktDocument;
		} catch (_e) {
			this.toastr.error('Punkt konnte nicht geladen werden.');
			return { ok: false };
		}
		if (!fresh?.header) {
			this.toastr.error('Punkt nicht gefunden.');
			return { ok: false };
		}

		// Welche anderen Projekte verliert der Punkt durch das Umhängen?
		const oldMemberships = (fresh.header.projects ?? []).filter(p => p.id !== projectId);
		const oldWorkflowProjectId = fresh.workflow?.projectId;
		// Name des Quell-Projekts für Dialog + Reset-Marker: bevorzugt das Projekt,
		// dem der Workflow gehört; sonst die erste Fremd-Mitgliedschaft.
		const fromName =
			oldMemberships.find(p => p.id === oldWorkflowProjectId)?.name
			?? oldMemberships[0]?.name;

		// Reaktivierung (2026-06-17): Liegt der Punkt bereits (nur) im aktiven
		// Projekt, ist das KEIN Umhängen aus einem anderen Projekt, sondern eine
		// Freigabe zurück in den Pool — dann spricht der Dialog von „freigeben"
		// statt „übernehmen aus X" (sonst irreführend, weil es kein Quell-Projekt
		// gibt). Greift nur, wenn es kein Fremd-Projekt zu verlassen gibt (fromName
		// leer); ein echtes Umhängen behält die Entzugs-Meldung.
		const isReactivation = !fromName
			&& (fresh.header.projects ?? []).some(p => p.id === projectId);

		const message = fromName
			? `Der Punkt wird aus „${fromName}" entfernt und in „${projectName}" übernommen — `
				+ `der Workflow wird zurückgesetzt (die bisherige Historie bleibt als Reset-Marker erhalten). Fortfahren?`
			: isReactivation
				? `Der Punkt wird in „${projectName}" wieder zur Bearbeitung freigegeben (zurück in den Pool „Anstehend") — `
					+ `der Workflow wird zurückgesetzt (die bisherige Historie bleibt als Reset-Marker erhalten). `
					+ `Ein Bearbeiter kann ihn danach mit „In Bearbeitung nehmen" übernehmen. Fortfahren?`
				: `Der Punkt wird in „${projectName}" übernommen — `
					+ `der Workflow wird zurückgesetzt (die bisherige Historie bleibt als Reset-Marker erhalten). Fortfahren?`;

		const confirmed = await this.confirmService.confirm({
			title: isReactivation ? 'Zur Bearbeitung freigeben' : 'In dieses Projekt übernehmen',
			message,
			confirmText: isReactivation ? 'Freigeben' : 'Übernehmen',
			confirmClass: 'btn-success',
			cancelText: 'Abbrechen'
		});
		if (confirmed !== true) return { ok: false };

		let saved: AufnahmePunktDocument;
		try {
			// Umhängen: Workflow-Reset ins aktive Projekt, header.projects = [aktiv],
			// Reset-Marker mit fromProjectName. Liefert die verlassenen Projekte.
			const leaving = applyTakeoverMove(
				fresh,
				{ id: projectId, name: projectName },
				this.auth.currentUser,
				this.auth.currentUserId ?? ''
			);

			saved = await this.data.put(fresh) as AufnahmePunktDocument;

			// Abgeleitete Projekt-Status: alte Projekte (verlieren den Punkt) + neues.
			for (const pid of leaving) {
				await this.workflow.refreshDerivedProjectStatus(pid);
			}
			await this.workflow.refreshDerivedProjectStatus(projectId);
		} catch (_e) {
			this.toastr.error('Übernahme fehlgeschlagen.');
			return { ok: false };
		}

		this.toastr.success('Punkt in dieses Projekt übernommen');
		return { ok: true, doc: saved };
	}

	/**
	 * Umkehrung von `freigeben` (User-Wunsch 2026-06-19): nimmt eine Übernahme
	 * zurück. Der per Übernahme (abgeschlossen → anstehend) ins aktive Projekt
	 * geholte Punkt wird wieder auf `abgeschlossen` gesetzt und seinem **vorigen
	 * Projekt** zugeordnet (aus dem Reset-Marker). Existiert das vorige Projekt
	 * nicht mehr, wird der Punkt projektlos → **Bestandspunkt**
	 * (`header.projects = []`, kein `workflow.projectId`).
	 *
	 * Rechte (Sichtbarkeit des Buttons) prüfen die Aufrufer über
	 * `ProjectService.canIzuruecknehmenPoint()` (identisch zur Übernahme:
	 * Manager/Entwickler) — hier wird nur ausgeführt. Die Erkennung „echte
	 * Übernahme + voriges Projekt" liefert `takeoverPreviousProject` (Pool-
	 * Rückgaben bearbeitung → anstehend sind keine Übernahmen → `null`).
	 */
	async zuruecknehmen(bp: AufnahmePunktDocument): Promise<FreigabeResult> {
		const projectId = this.ps.project();
		if (!projectId) {
			this.toastr.error('Kein aktives Projekt.');
			return { ok: false };
		}

		let fresh: AufnahmePunktDocument;
		try {
			fresh = await this.data.get(bp._id) as AufnahmePunktDocument;
		} catch (_e) {
			this.toastr.error('Punkt konnte nicht geladen werden.');
			return { ok: false };
		}
		if (!fresh?.header) {
			this.toastr.error('Punkt nicht gefunden.');
			return { ok: false };
		}

		const prev = takeoverPreviousProject(fresh);
		if (!prev) {
			this.toastr.error('Dieser Punkt ist keine rücknehmbare Übernahme.');
			return { ok: false };
		}

		// Existiert das vorige Projekt noch? Sonst wird der Punkt Bestand. Die
		// Projekt-Metadaten liegen in der Projekt-DB (ProjectService.getOne) —
		// 404/leeres Ergebnis = Projekt gelöscht → Bestand.
		let restoreTo: { id: string; name?: string } | null = null;
		if (prev.id) {
			try {
				const pdoc = await this.ps.getOne(prev.id) as { name?: string } | undefined;
				if (pdoc) restoreTo = { id: prev.id, name: pdoc.name ?? prev.name };
			} catch (_e) {
				restoreTo = null; // Projekt existiert nicht mehr → Bestand
			}
		}

		const baseMsg = restoreTo
			? `Die Übernahme wird zurückgenommen — der Punkt geht zurück auf „Abgeschlossen" und wird wieder „${restoreTo.name ?? restoreTo.id}" zugeordnet.`
			: `Die Übernahme wird zurückgenommen — der Punkt geht zurück auf „Abgeschlossen". Das vorige Projekt existiert nicht mehr, daher wird er ein Bestandspunkt (keinem Projekt zugeordnet).`;
		// Warnung (Bestand-Ausnahme, User-Entscheidung 2026-06-19): wurde der Punkt
		// nach der Übernahme noch gegriffen/bearbeitet, reisen etwaige Änderungen als
		// „Abgeschlossen" mit ins vorige Projekt, ohne dort erneut kontrolliert zu
		// werden. Nur solche Punkte sind über die Bestand-Ausnahme überhaupt rücknehmbar.
		const interim = takeoverHadInterimWork(fresh);
		const message = `${baseMsg}${interim
			? ' ⚠ Dieser Punkt wurde nach der Übernahme bearbeitet — etwaige Änderungen reisen als „Abgeschlossen" mit und werden im Zielprojekt NICHT erneut kontrolliert.'
			: ''} Fortfahren?`;

		const confirmed = await this.confirmService.confirm({
			title: 'Übernahme zurücknehmen',
			message,
			confirmText: 'Zurücknehmen',
			confirmClass: interim ? 'btn-danger' : 'btn-warning',
			cancelText: 'Abbrechen'
		});
		if (confirmed !== true) return { ok: false };

		let saved: AufnahmePunktDocument;
		try {
			// Zurückhängen: Workflow auf abgeschlossen, header.projects = [voriges
			// Projekt] (oder [] → Bestand). Liefert das verlassene Projekt (aktiv).
			const leaving = applyTakeoverRevert(
				fresh,
				restoreTo,
				this.auth.currentUser,
				this.auth.currentUserId ?? ''
			);

			saved = await this.data.put(fresh) as AufnahmePunktDocument;

			// Abgeleitete Projekt-Status: verlassenes (aktiv) + Ziel (voriges).
			for (const pid of leaving) {
				await this.workflow.refreshDerivedProjectStatus(pid);
			}
			if (restoreTo) {
				await this.workflow.refreshDerivedProjectStatus(restoreTo.id);
			}
		} catch (_e) {
			this.toastr.error('Zurücknehmen fehlgeschlagen.');
			return { ok: false };
		}

		this.toastr.success(restoreTo
			? 'Übernahme zurückgenommen'
			: 'Übernahme zurückgenommen — Punkt ist jetzt Bestand');
		return { ok: true, doc: saved };
	}
}
